import { Link } from "react-router-dom";
import { Phone, MapPin, Clock, Mail, Instagram, Facebook, Linkedin } from "lucide-react";
import { assets, business } from "../../data/site";
import { useOrder } from "../OrderProvider";
import BrandLogo from "../BrandLogo";

const TikTokIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V9.02a8.16 8.16 0 0 0 4.77 1.52V7.1a4.85 4.85 0 0 1-1.84-.41Z" />
  </svg>
);

export default function Footer() {
  const openOrder = useOrder();
  return (
    <footer data-testid="site-footer" className="bg-[#0a0a0a] border-t border-white/10 relative z-10">
      <div className="max-w-7xl mx-auto px-5 md:px-10 py-10 md:py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 md:gap-8">
          {/* Brand */}
          <div className="md:col-span-1">
            <BrandLogo src={assets.logo} size="lg" className="mb-4" />
            <p className="text-neutral-400 text-base leading-relaxed">
              Authentic Indian food in Ottawa since {business.since}. Veg, Vegan, Halal & Non-Veg.
            </p>
            <div className="flex flex-wrap gap-3 mt-5">
              <a href={business.instagram} target="_blank" rel="noreferrer" aria-label="Instagram"
                className="w-11 h-11 grid place-items-center rounded-full border border-white/15 text-white hover:bg-[#ffd100] hover:text-black hover:border-[#ffd100] transition-colors">
                <Instagram size={20} />
              </a>
              <a href={business.facebook} target="_blank" rel="noreferrer" aria-label="Facebook"
                className="w-11 h-11 grid place-items-center rounded-full border border-white/15 text-white hover:bg-[#ffd100] hover:text-black hover:border-[#ffd100] transition-colors">
                <Facebook size={20} />
              </a>
              <a href={business.tiktok} target="_blank" rel="noreferrer" aria-label="TikTok"
                data-testid="footer-tiktok"
                className="w-11 h-11 grid place-items-center rounded-full border border-white/15 text-white hover:bg-[#ffd100] hover:text-black hover:border-[#ffd100] transition-colors">
                <TikTokIcon size={20} />
              </a>
              <a href={business.linkedin} target="_blank" rel="noreferrer" aria-label="LinkedIn"
                data-testid="footer-linkedin"
                className="w-11 h-11 grid place-items-center rounded-full border border-white/15 text-white hover:bg-[#ffd100] hover:text-black hover:border-[#ffd100] transition-colors">
                <Linkedin size={20} />
              </a>
            </div>
          </div>

          {/* Explore */}
          <div>
            <h4 className="font-display text-lg font-semibold text-[#ffd100] mb-4">Explore</h4>
            <ul className="space-y-3 text-neutral-300 text-base">
              <li><Link to="/menu" className="hover:text-[#ffd100] transition-colors">Menu</Link></li>
              <li><Link to="/catering" className="hover:text-[#ffd100] transition-colors">Catering</Link></li>
              <li><Link to="/featured" className="hover:text-[#ffd100] transition-colors">Food Bloggers</Link></li>
              <li><Link to="/about" className="hover:text-[#ffd100] transition-colors">About Us</Link></li>
              <li><Link to="/blog" className="hover:text-[#ffd100] transition-colors">Blog</Link></li>
              <li><Link to="/contact" className="hover:text-[#ffd100] transition-colors">Contact</Link></li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-display text-lg font-semibold text-[#ffd100] mb-4">Visit Us</h4>
            <ul className="space-y-4 text-neutral-300 text-base">
              <li className="flex gap-3"><MapPin size={20} className="text-[#ffd100] shrink-0 mt-0.5" /> <span>{business.addressLine1}<br />{business.addressLine2}</span></li>
              <li className="flex gap-3"><Clock size={20} className="text-[#ffd100] shrink-0 mt-0.5" /> {business.hours}</li>
              <li className="flex gap-3"><Mail size={20} className="text-[#ffd100] shrink-0 mt-0.5" /> {business.email}</li>
            </ul>
          </div>

          {/* Call */}
          <div>
            <h4 className="font-display text-lg font-semibold text-[#ffd100] mb-4">Order & Enquire</h4>
            <a href={`tel:${business.phoneRaw}`} className="flex items-center gap-2 text-2xl font-bold text-white hover:text-[#ffd100] transition-colors mb-2">
              <Phone size={22} /> {business.phone}
            </a>
            <p className="text-neutral-400 text-base">WhatsApp: <a href={`https://wa.me/${business.whatsappRaw}`} target="_blank" rel="noreferrer" className="text-white hover:text-[#ffd100] transition-colors">{business.whatsapp}</a></p>
            <button type="button" onClick={openOrder} data-testid="footer-order-cta"
              className="inline-block mt-5 bg-[#ffd100] text-[#0a0a0a] font-bold px-6 py-3 rounded-full hover:bg-[#e5bc00] transition-colors">
              Order Online
            </button>
          </div>
        </div>

        <div className="mt-8 pt-6 border-t border-white/10 flex flex-col md:flex-row justify-between gap-3 text-neutral-500 text-sm">
          <p>© {new Date().getFullYear()} {business.name}. All rights reserved.</p>
          <p>
            Designed by{" "}
            <a href="https://avefy.ca" target="_blank" rel="noreferrer"
              className="text-[#ffd100] hover:underline font-medium">
              Avefy Corp
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
}
